import { Outlet, useNavigate, useParams } from 'react-router-dom'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import {
  DocumentFragment,
  PayrollCalculationDocumentsDocument,
  PayrollDocumentFragment,
  PayrollExportFragment,
  PayrollPoliciesDocument,
} from 'generated/resources/graphql'

import NotFound from 'modules/core/components/NotFound'
import createComputed from 'modules/core/hooks/createComputed'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import { useIncidenceFilterContext } from 'modules/bookkeepers/lib/IncidenceFilterContext'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import type PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'

import PayrollCalculationStep from './_PayrollCalculationStep'
import PayrollPeriodSelector from './_PayrollPeriodSelector'
import PayrollPoliciesTabs from './_PayrollPoliciesTabs'

export interface OutletProps {
  selectedCompany: number | null
  currentPolicyPeriod?: PolicyPeriod
  onUploadCompleted: () => void
}

interface SortedDocuments {
  downloadedDocuments: DocumentFragment[] | null
  uploadedDocuments: DocumentFragment[]
}

const sortDocuments = createComputed(
  (
    payrollDocuments: PayrollDocumentFragment[],
    payrollExportDocuments: PayrollExportFragment[]
  ): SortedDocuments => {
    const downloadedDocuments: DocumentFragment[] = payrollExportDocuments
      .map((document) => document.document)
      .filter((document) => !!document)

    const uploadedDocuments: DocumentFragment[] = payrollDocuments
      .map((document) => document.document)
      .filter((document) => !!document)

    return { downloadedDocuments, uploadedDocuments }
  }
)

const PayrollExportsPage = () => {
  const { selectedCompany: filterContextCompany } = useIncidenceFilterContext()
  const selectedCompanyId = filterContextCompany?.attributes.id
  const navigate = useNavigate()
  const bookkeeperContext = {
    isBookkeeperView: true,
  }

  const { policyId, periodId } = useParams()

  const periodIdNumber = periodId ?? ''
  const policyIdNumber = policyId ?? ''
  const {
    loadingPolicyPeriodCollection: fetchingPeriods,
    periodIndex,
    currentPolicyPeriods,
  } = usePolicyPeriods()
  const { navigation } = useNavigation()

  const currentPolicyPeriod = currentPolicyPeriods[periodIndex]

  const {
    data: documentsData,
    loading: fetchingDocuments,
    refetch: refetchDocuments,
  } = useQuery(PayrollCalculationDocumentsDocument, {
    context: bookkeeperContext,
    variables: {
      policyPeriodIds: currentPolicyPeriod ? [currentPolicyPeriod.id] : [],
    },
  })

  const { data: policiesData, loading: fetchingPolicies } = useQuery(PayrollPoliciesDocument, {
    context: bookkeeperContext,
    variables: {
      policyIds: [policyIdNumber],
    },
    skip: !policyId,
  })

  const policies = policiesData?.payroll.policiesConnection.nodes || []
  const currentPolicy = policies.find((policy) => policy.id === policyIdNumber)
  const policyCompanyId = currentPolicy?.company.id
  const loading = fetchingPolicies || fetchingDocuments || fetchingPeriods

  useEffect(() => {
    if (loading) {
      return
    }

    if (selectedCompanyId !== policyCompanyId) {
      // No company selected but instead get the company from the policy
      if (!selectedCompanyId && policyCompanyId) {
        return navigate(
          navigation.bookkeepers.clients.payrollResults.payrollPolicy.payrollPolicyPeriodId.fullPath(
            {
              periodId: policyIdNumber,
              policyId: currentPolicy.id,
            }
          )
        )
      }

      navigate(navigation.bookkeepers.clients.payrollResults.fullPath())
    }
  }, [loading, selectedCompanyId, policyId]) // eslint-disable-line react-hooks/exhaustive-deps

  const onClose = () => {
    return navigate(
      navigation.bookkeepers.clients.payrollResults.payrollPolicy.payrollPolicyPeriodId.fullPath({
        periodId: currentPolicyPeriod?.attributes.id,
        policyId: policyIdNumber,
      })
    )
  }

  const payrollDocuments = documentsData?.payroll.payrollDocumentsConnection.nodes || []
  const payrollExportDocuments = documentsData?.payrollExporter.exportsConnection.nodes || []
  const { downloadedDocuments, uploadedDocuments } = sortDocuments(
    payrollDocuments,
    payrollExportDocuments
  ).get()

  if (!selectedCompanyId && !policyCompanyId && !fetchingPolicies) {
    return <NotFound />
  }

  if (fetchingPeriods || !policyId) {
    return <Spinner />
  }

  return (
    <Box>
      <Outlet
        context={{
          selectedCompanyId,
          currentPolicyPeriod,
          onClose,
          onUploadCompleted: refetchDocuments,
        }}
      />
      <PayrollPoliciesTabs companyId={(selectedCompanyId || policyCompanyId) ?? ''} />
      <PayrollPeriodSelector
        selectedPolicyId={policyIdNumber}
        currentPeriod={currentPolicyPeriod}
        periodId={periodIdNumber}
      />
      {currentPolicyPeriod && (
        <PayrollCalculationStep
          policyId={policyIdNumber}
          loading={fetchingDocuments || fetchingPolicies}
          documents={uploadedDocuments}
          payrollDocuments={payrollDocuments}
          reloadFiles={refetchDocuments}
          periodId={periodIdNumber}
          downloadedDocuments={downloadedDocuments}
          companyId={(selectedCompanyId || policyCompanyId) ?? ''}
        />
      )}
    </Box>
  )
}

export default PayrollExportsPage
